// The local copy: every project this device knows, in memory for the screens
// and in IndexedDB for the next launch. Edits apply here first and queue in
// the outbox until the list acknowledges them. The store is the only thing
// that touches IndexedDB, and it knows nothing of React: hooks.ts adapts it.
//
// What the list sends back (app/sync/) lands here too, never in the outbox:
// another device's changes and a run's grafts in sequence order, the whole
// list when this device is new or too far behind, and each project's latest
// run. The last sequence applied is kept in meta, so a reopened device asks
// only for what it missed, and so is the list this copy belongs to, so a
// device never takes one person's copy into another person's list
// (app/session.ts).
//
// The outbox is in IndexedDB beside the documents, written in the same
// transaction as the edit that made it, so a change that has not synced
// survives a reload, a closed tab and a restart; the first write asks the
// browser to keep our data through storage pressure. A change leaves it when
// the list acknowledges it, or refuses it for good (refuse(), below: kept in
// meta as the last few refusals, so a screen could say what did not sync).
//
// Signing out wipes it (wipe(), below): the database erased and the app's
// store replaced by a fresh one, seeded with the demo projects under a new
// device id, so the next person on a shared device starts as a visitor.
import { DEMO_PROJECTS } from '@shared/demo';
import type { RunProgress } from '@shared/artifacts';
import { applyChange, type Change, type Edit } from '@shared/edit';
import type { Id, Millis, Project } from '@shared/plan';
import type { Logged } from '@shared/socket';
import { eraseStoreDb, openStoreDb, type ProjectRow, type StoreDb } from './db';

export interface Store {
  /** Resolves once the database is open, seeded if it was empty, and loaded. */
  ready: Promise<void>;
  /** Stable per browser profile; empty until `ready`. Stamped on every Change. */
  deviceId: string;
  /** Every project, tombstones included, newest createdAt first. The same array until something changes. */
  projects(): readonly Project[];
  get(id: Id): Project | undefined;
  /** Stamps id, at and deviceId, applies, writes the document and the outbox entry together, notifies. */
  apply(projectId: Id, edit: Edit): Promise<Change>;
  /** For useSyncExternalStore. */
  subscribe(listener: () => void): () => void;

  // --- What the list says (app/sync/ calls these).

  /** The last list sequence this device applied; 0 before its first sync, or while it needs the whole list. Kept across launches. */
  lastSeq(): number;
  /** The list this device's copy belongs to: null for a visitor's, until the move into a list lands. Kept across launches; a wipe forgets it. */
  listId(): string | null;
  /** This device's copy now belongs to `listId` (its move landed). */
  setListId(listId: string): Promise<void>;
  /** The latest run of each project, by project id, as the list last said. The same Map until something changes. */
  runs(): ReadonlyMap<Id, RunProgress>;
  /**
   * Changes the list logged, applied in sequence order through applyChange
   * and never written to the outbox. A sequence already applied is skipped.
   * False when one could not be applied here (an edit this build does not
   * know yet): the device should take the whole list instead, and until it
   * does, lastSeq() is 0 and nothing more is applied.
   */
  applyRemote(changes: readonly Logged[]): Promise<boolean>;
  /**
   * The whole list. Each project the list has replaces this device's copy,
   * with the outbox's changes for it applied again on top, so nothing made
   * here and not yet acknowledged is lost; a project only this device has
   * (made offline, or a demo project never moved in) stays as it is. When
   * `runs` is given it replaces every run.
   */
  takeSnapshot(projects: readonly Project[], seq: number, runs?: readonly RunProgress[]): Promise<void>;
  /** A run's progress: it becomes its project's latest unless an older run's late report. */
  setRun(run: RunProgress): void;
  /** The list holds this change now, so it leaves the outbox. */
  acknowledge(changeId: string): Promise<void>;
  /**
   * The list will never take this change as it is (its shape, or a kind only
   * the server writes): it leaves the outbox, so it is not sent for ever,
   * and joins refused(). This device's copy keeps the edit until the list's
   * copy replaces it (sync asks for the whole list after a refusal).
   */
  refuse(changeId: string, reason: string): Promise<void>;
  /** The last REFUSED_KEPT changes the list refused, oldest first, kept across launches. */
  refused(): readonly Refusal[];
  /** The outbox, oldest first: every change the list has not acknowledged. */
  pending(): readonly Change[];
  /** Called with each Change this device makes, once it is in the outbox. Sync sends it live. */
  onApplied(listener: (change: Change) => void): () => void;

  media: {
    get(hash: string): Promise<Blob | undefined>;
    put(hash: string, blob: Blob): Promise<void>;
  };

  /**
   * Lets go of the database once the work in hand has finished. From then
   * on every write, and every read of the database, rejects, and nothing is
   * applied in memory either. wipe() closes the app's store this way.
   */
  close(): Promise<void>;
}

/** A change the list refused for good, and its words for why. */
export interface Refusal { change: Change; reason: string; at: Millis }

/** How many refusals refused() remembers. */
export const REFUSED_KEPT = 20;

export interface StoreOptions {
  /** An open database, or one on its way; the app's own by default. */
  db?: StoreDb | Promise<StoreDb>;
  now?: () => Millis;
  /** What an empty store starts with. */
  seed?: readonly Project[];
  /** Fetches the bytes behind a seeded photoHash the cache lacks; undefined never fetches. */
  fetchMedia?: (hash: string) => Promise<Blob | undefined>;
  /** Asks the browser to keep our data through storage pressure; undefined never asks. */
  persist?: () => Promise<boolean>;
}

interface OutboxEntry {
  change: Change;
  /** Its key in IndexedDB, once written. */
  key?: number;
  /** The write, while it has not landed. */
  written?: Promise<number>;
}

/** An entry's key, once its write has landed; undefined if the write failed (nothing to delete). */
async function keyOf(entry: OutboxEntry): Promise<number | undefined> {
  if (entry.key !== undefined || !entry.written) return entry.key;
  try {
    return await entry.written;
  } catch {
    return undefined;
  }
}

/** A seeded photo ships with the app under its content hash. */
async function fetchDemoPhoto(hash: string): Promise<Blob | undefined> {
  const response = await fetch(`/art/demo/${hash}.jpg`);
  return response.ok ? response.blob() : undefined;
}

async function persistInBrowser(): Promise<boolean> {
  const storage = typeof navigator === 'undefined' ? undefined : navigator.storage;
  if (!storage?.persist) return false;
  return (await storage.persisted()) || storage.persist();
}

const clock = (stamp: Millis | undefined): number => stamp ?? Number.NEGATIVE_INFINITY;
/** Well after the first paint: in a browser, once the page has loaded and sat idle for a moment. */
const whenIdle = (f: () => void): void => {
  if (typeof document === 'undefined') return void setTimeout(f, 0);
  const later = () => setTimeout(f, 3000);
  if (document.readyState === 'complete') later();
  else addEventListener('load', later, { once: true });
};
/** Newest first; a tie keeps id order, which for the demo projects is their DemoData order. */
const newestFirst = (a: Project, b: Project): number => clock(b.createdAt) - clock(a.createdAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export function createStore(opts: StoreOptions = {}): Store {
  const now = opts.now ?? Date.now;
  const fetchMedia = opts.fetchMedia ?? (typeof fetch === 'function' ? fetchDemoPhoto : undefined);
  const persist = opts.persist ?? persistInBrowser;
  const listeners = new Set<() => void>();
  const appliedListeners = new Set<(change: Change) => void>();
  const rows = new Map<Id, ProjectRow>();
  /** The outbox in memory, in order, by change id: its key in IndexedDB once the write has landed, and the write itself until then. */
  const outbox = new Map<string, OutboxEntry>();
  let refusals: readonly Refusal[] = Object.freeze([]);
  let list: readonly Project[] = Object.freeze([]);
  let deviceId = '';
  let seq = 0;
  let heldList: string | null = null;
  /** A remote change could not be applied here: nothing more is, until the whole list arrives. */
  let stale = false;
  let runMap: ReadonlyMap<Id, RunProgress> = new Map();
  let persistAsked = false;
  let closed = false;
  let db!: StoreDb;

  const notify = () => { for (const listener of listeners) listener(); };
  /** Every async method calls this after `await ready`, before it touches memory or the database. */
  const open = () => { if (closed) throw new Error('The device store is closed.'); };
  const refresh = () => { list = Object.freeze([...rows.values()].map((r) => r.doc).sort(newestFirst)); };

  const ready = (async () => {
    db = await (opts.db ?? openStoreDb());
    const tx = db.transaction(['projects', 'meta', 'outbox'], 'readwrite');
    const meta = tx.objectStore('meta'), projects = tx.objectStore('projects');
    let id = (await meta.get('deviceId')) as string | undefined;
    if (!id) {
      id = crypto.randomUUID();
      await meta.put(id, 'deviceId');
    }
    deviceId = id;
    seq = ((await meta.get('lastSeq')) as number | undefined) ?? 0;
    heldList = ((await meta.get('listId')) as string | undefined) ?? null;
    refusals = Object.freeze(((await meta.get('refused')) as Refusal[] | undefined) ?? []);
    for (let cursor = await tx.objectStore('outbox').openCursor(); cursor; cursor = await cursor.continue()) {
      outbox.set(cursor.value.id, { key: cursor.key, change: cursor.value });
    }
    // An empty store is a first visit: it starts with the demo projects, under
    // their fixed ids, created now and never edited (so any real edit beats the seed).
    let seeded: readonly Project[] = [];
    if ((await projects.count()) === 0) {
      const at = now();
      seeded = (opts.seed ?? DEMO_PROJECTS).map((p) => ({ ...p, createdAt: at }));
      for (const doc of seeded) await projects.put({ doc, rev: 0 }, doc.id);
    }
    for (const row of await projects.getAll()) rows.set(row.doc.id, row);
    await tx.done;
    refresh();
    notify();
    // After the first paint, not before it: the demo photo is 400 KB and
    // must not share the first load's bandwidth with the shell.
    whenIdle(() => void cacheSeedPhotos(seeded));
  })();

  /** Lazily and without fuss: offline on a first visit, the photo arrives next time; closed meanwhile (a sign-out), never. */
  async function cacheSeedPhotos(seeded: readonly Project[]): Promise<void> {
    if (!fetchMedia) return;
    for (const p of seeded) {
      if (closed) return;
      try {
        if (!p.photoHash || (await db.get('media', p.photoHash))) continue;
        const blob = await fetchMedia(p.photoHash);
        if (blob && !closed) await db.put('media', blob, p.photoHash);
      } catch {
        // Not reachable now; the hash still names it for a later fetch.
      }
    }
  }

  const store: Store = {
    ready,
    get deviceId() { return deviceId; },
    projects: () => list,
    get: (id) => rows.get(id)?.doc,

    async apply(projectId, edit) {
      await ready;
      open();
      const change: Change = { id: crypto.randomUUID(), projectId, at: now(), deviceId, edit };
      const row = rows.get(projectId);
      if (!row && edit.kind !== 'create') throw new Error(`apply: no project ${projectId}`);
      const next = applyChange(row?.doc, change)!;
      // Memory first, with nothing awaited in between, so two quick ticks
      // each see the other's result and the screen moves at once; the write
      // follows, and the outbox gets every Change, even one that changed nothing here.
      const changed = next !== row?.doc;
      const rev = (row?.rev ?? 0) + (changed ? 1 : 0);
      const entry: OutboxEntry = { change };
      outbox.set(change.id, entry);
      if (changed) {
        rows.set(projectId, { doc: next, rev });
        refresh();
        notify();
      }
      if (!persistAsked) {
        persistAsked = true;
        void persist().catch(() => false);
      }
      const tx = db.transaction(['projects', 'outbox'], 'readwrite');
      if (changed) void tx.objectStore('projects').put({ doc: next, rev }, projectId);
      const added = tx.objectStore('outbox').add(change);
      // An ack can arrive before the write lands (sync sends what pending() shows); it waits for the key.
      entry.written = tx.done.then(() => added);
      entry.key = await entry.written;
      entry.written = undefined;
      for (const listener of appliedListeners) listener(change);
      return change;
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },

    lastSeq: () => (stale ? 0 : seq),
    listId: () => heldList,
    async setListId(listId) {
      await ready;
      open();
      await db.put('meta', listId, 'listId');
      heldList = listId;
    },
    runs: () => runMap,
    pending: () => [...outbox.values()].map((e) => e.change),

    async applyRemote(changes) {
      await ready;
      open();
      if (stale) return false;
      const touched = new Map<Id, ProjectRow>();
      let ok = true;
      for (const change of [...changes].sort((a, b) => a.seq - b.seq)) {
        if (change.seq <= seq) continue;
        const row = rows.get(change.projectId);
        let next: Project | undefined;
        try {
          next = applyChange(row?.doc, change);
        } catch {
          // Stop here: applying later changes past this one would leave a hole no hello could fill.
          ok = false;
          stale = true;
          break;
        }
        if (next && next !== row?.doc) {
          const updated = { doc: next, rev: (row?.rev ?? 0) + 1 };
          rows.set(change.projectId, updated);
          touched.set(change.projectId, updated);
        }
        seq = change.seq;
      }
      if (touched.size) {
        refresh();
        notify();
      }
      const tx = db.transaction(['projects', 'meta'], 'readwrite');
      for (const [id, row] of touched) void tx.objectStore('projects').put(row, id);
      void tx.objectStore('meta').put(seq, 'lastSeq');
      await tx.done;
      return ok;
    },

    async takeSnapshot(projects, listSeq, runs) {
      await ready;
      open();
      const mine = new Map<Id, Change[]>();
      for (const { change } of outbox.values()) mine.set(change.projectId, [...(mine.get(change.projectId) ?? []), change]);
      const changed = new Map<Id, ProjectRow>();
      for (const theirs of projects) {
        let doc: Project = theirs;
        for (const change of mine.get(theirs.id) ?? []) {
          try {
            doc = applyChange(doc, change) ?? doc;
          } catch {
            // An edit this build cannot apply; the list's copy stands.
          }
        }
        const row = rows.get(doc.id);
        if (row && JSON.stringify(row.doc) === JSON.stringify(doc)) continue;
        const updated = { doc, rev: (row?.rev ?? 0) + 1 };
        rows.set(doc.id, updated);
        changed.set(doc.id, updated);
      }
      seq = listSeq;
      stale = false;
      if (runs) runMap = new Map(runs.map((r) => [r.projectId, r]));
      if (changed.size) refresh();
      if (changed.size || runs) notify();
      const tx = db.transaction(['projects', 'meta'], 'readwrite');
      for (const [id, row] of changed) void tx.objectStore('projects').put(row, id);
      void tx.objectStore('meta').put(seq, 'lastSeq');
      await tx.done;
    },

    setRun(run) {
      if (closed) return;
      const existing = runMap.get(run.projectId);
      if (existing && existing.runId !== run.runId && existing.startedAt > run.startedAt) return;
      runMap = new Map(runMap).set(run.projectId, run);
      notify();
    },

    async acknowledge(changeId) {
      await ready;
      open();
      const entry = outbox.get(changeId);
      if (!entry) return;
      outbox.delete(changeId);
      const key = await keyOf(entry);
      if (key !== undefined) await db.delete('outbox', key);
    },

    async refuse(changeId, reason) {
      await ready;
      open();
      const entry = outbox.get(changeId);
      if (!entry) return;
      outbox.delete(changeId);
      refusals = Object.freeze([...refusals, { change: entry.change, reason, at: now() }].slice(-REFUSED_KEPT));
      const key = await keyOf(entry);
      const tx = db.transaction(['outbox', 'meta'], 'readwrite');
      if (key !== undefined) void tx.objectStore('outbox').delete(key);
      void tx.objectStore('meta').put(refusals, 'refused');
      await tx.done;
    },

    refused: () => refusals,

    onApplied(listener) {
      appliedListeners.add(listener);
      return () => { appliedListeners.delete(listener); };
    },

    media: {
      async get(hash) {
        await ready;
        open();
        return db.get('media', hash);
      },
      async put(hash, blob) {
        await ready;
        open();
        await db.put('media', blob, hash);
      },
    },

    async close() {
      closed = true;
      try {
        await ready;
      } catch {
        return; // never opened
      }
      // The connection closes once its running transactions finish.
      db.close();
    },
  };
  return store;
}

// The app's one store, opened on first use rather than on import, so a test
// or a Worker build that imports this module touches no database. Screens and
// sync subscribe here, not to the instance, so they follow the store across a
// wipe; only the current instance's news reaches them.
let singleton: Store | undefined;
const shown = new Set<() => void>();
const heard = new Set<(change: Change) => void>();

function adopt(instance: Store): Store {
  instance.subscribe(() => {
    if (singleton === instance) for (const listener of shown) listener();
  });
  instance.onApplied((change) => {
    if (singleton === instance) for (const listener of heard) listener(change);
  });
  return instance;
}

const live = (): Store => (singleton ??= adopt(createStore()));

export const store: Store = {
  get ready() { return live().ready; },
  get deviceId() { return live().deviceId; },
  projects: () => live().projects(),
  get: (id) => live().get(id),
  apply: (projectId, edit) => live().apply(projectId, edit),
  subscribe(listener) {
    live();
    shown.add(listener);
    return () => { shown.delete(listener); };
  },
  lastSeq: () => live().lastSeq(),
  listId: () => live().listId(),
  setListId: (listId) => live().setListId(listId),
  runs: () => live().runs(),
  applyRemote: (changes) => live().applyRemote(changes),
  takeSnapshot: (projects, seq, runs) => live().takeSnapshot(projects, seq, runs),
  setRun: (run) => live().setRun(run),
  acknowledge: (changeId) => live().acknowledge(changeId),
  refuse: (changeId, reason) => live().refuse(changeId, reason),
  refused: () => live().refused(),
  pending: () => live().pending(),
  onApplied(listener) {
    live();
    heard.add(listener);
    return () => { heard.delete(listener); };
  },
  media: {
    get: (hash) => live().media.get(hash),
    put: (hash, blob) => live().media.put(hash, blob),
  },
  close: async () => { await singleton?.close(); },
};

let wiping: Promise<void> | undefined;

/**
 * Signing out on this device: the app's store closed (the work in hand
 * finishes, anything after it rejects), the database erased, and a fresh
 * store in its place, seeded with the demo projects under a new device id.
 * Screens re-render from it once it is loaded.
 *
 * Call it after sync has stopped (signOut() sets meStore to null first, and
 * the session stops sync at once). A message sync had already queued then
 * reaches the closed store and fails, since the swap waits on the database,
 * after the queue has run; nothing from before the sign-out lands in the
 * fresh store. Calls while one is running share it.
 */
export function wipe(): Promise<void> {
  wiping ??= (async () => {
    // No IndexedDB (a test in node, a browser with storage off): nothing was kept.
    if (typeof indexedDB === 'undefined') return;
    await singleton?.close();
    await eraseStoreDb();
    const fresh = adopt(createStore());
    singleton = fresh;
    await fresh.ready;
  })().finally(() => { wiping = undefined; });
  return wiping;
}

/** Projects with a run going now: the home list's Building group. */
export function buildingIds(runs: ReadonlyMap<Id, RunProgress>): Set<Id> {
  return new Set([...runs.values()].filter((r) => r.kind !== 'snag' && r.status === 'running').map((r) => r.projectId));
}

/** Projects whose latest plan run failed: the home list's Needs a look group. A failed snag is the step's, not the project's. */
export function failedIds(runs: ReadonlyMap<Id, RunProgress>): Set<Id> {
  return new Set([...runs.values()].filter((r) => r.kind !== 'snag' && r.status === 'failed').map((r) => r.projectId));
}
